import { useAppDispatch } from '@/app/redux/hooks';
import { setAttemptedProFeature } from '@/app/redux/slices/modalsSlice';
import {
  Grapheme,
  GraphemeId,
  MorphemeType,
  ScopeAndSequence,
  ScopeAndSequenceLevel,
  Sentence,
  SentenceId,
  Word,
  WordId,
} from '@/common/types/scopeAndSequence';
import {
  DashboardViewActions,
  DashboardViewContainer,
  DashboardViewContent,
  DashboardViewHeader,
  DashboardViewTitle,
} from '@/common/ui/DashboardViewStyles';
import { useIsFeatureEnabled } from '@/common/ui/FeatureGate';
import MicroAppNav from '@/common/ui/MicroAppNav';
import SearchInput from '@/common/ui/SearchInput';
import { ProIcon } from '@/common/ui/UpgradeButton';
import {
  playPhonemeAudio,
  playSentenceAudio,
  playTrickyWordAudio,
  soundOutSentence,
  stopAudio,
} from '@/common/utils/audio';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  ArrowRightIcon,
  ChalkboardTeacherIcon,
  JoystickIcon,
  SpeakerHighIcon,
} from '@phosphor-icons/react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@shadcn/components/accordion';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import { Separator } from '@shadcn/components/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { ChevronDown } from 'lucide-react';
import { forwardRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import scopeAndSequenceData from '../common/data/scopeAndSequence.json';
import { soundOutScopeAndSequenceWord } from '../common/utils/helpers';
import {
  getGraphemeById,
  getGraphemeOffsetInWord,
  getLevelById,
  getMorphemeById,
  getPhaseById,
  getSentenceById,
  getWordById,
  getWordGraphemes,
} from '../common/utils/scopeAndSequence';

const gameInfo: Record<string, { name: string; description: string }> = {
  mysticMatch: {
    name: 'Mystic Match',
    description:
      'Grapheme-phoneme matching. Learners hear a sound and match it to the correct card. Can include articulatory gesture support (mouth shapes).',
  },
  phonemeForge: {
    name: 'Phoneme Forge',
    description:
      'Word chains. Learners manipulate individual phonemes to transform one word into another (e.g. bend → bent → sent → rent).',
  },
  wordWeaver: {
    name: 'Word Weaver',
    description:
      'Word building. Learners construct words using card selection or full keyboard typing. Keyboard mode increases orthographic challenge by removing the scaffold of pre-selected options.',
  },
  trickyWords: {
    name: 'Heart Words',
    description:
      'Introduces irregular or unfamiliar words needed for upcoming connected text work. Kept minimal and deliberate.',
  },
  sentenceBuilder: {
    name: 'Sentence Builder',
    description:
      'Connected text encoding. Learners hear a sentence and build it using cards or typing. Sentences only contain code and morphemes encountered up to that point.',
  },
  wordRifts: {
    name: 'Word Rifts',
    description:
      'Cloze activity. Learners complete words or sentences with missing elements.',
  },
  wordSums: {
    name: 'Word Sums',
    description:
      'Learners build words from morphemic units rather than individual graphemes, highlighting natural cut points (e.g. afford + able).',
  },
  morphMaster: {
    name: 'Morph Master',
    description:
      'Morphological word chains. Learners transform words by swapping whole morphemic chunks (e.g. read → readable → breakable → unbreakable).',
  },
};

interface PhaseMap {
  [phase: number]: {
    levels: ScopeAndSequenceLevel[];
    description: string;
    levelRange: string;
  };
}

export default function ScopeAndSequenceView({
  standalone,
}: {
  standalone?: boolean;
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isFeatureEnabled = useIsFeatureEnabled();
  const isCustomLevelsEnabled = standalone || isFeatureEnabled('custom-levels');

  const [openPhases, setOpenPhases] = useState<string[]>(['phase-1']);
  const [searchTerm, setSearchTerm] = useState('');

  const scopeAndSequence = scopeAndSequenceData as ScopeAndSequence;

  // Group levels by phase
  const levelsByPhase = scopeAndSequence.levels.reduce<PhaseMap>(
    (acc, level) => {
      const phaseId = level.phase;
      const phase = getPhaseById(phaseId);
      if (!phase) {
        return acc;
      }
      if (!acc[phase.phase]) {
        const startLevel = getLevelById(phase.levels[0]);
        const endLevel = getLevelById(phase.levels[phase.levels.length - 1]);
        let levelRange = '';
        if (startLevel && endLevel) {
          levelRange = `Levels ${startLevel.level}-${endLevel.level}`;
        }
        acc[phase.phase] = {
          levels: [],
          description: phase.description || '',
          levelRange,
        };
      }
      acc[phase.phase].levels.push(level);
      return acc;
    },
    {},
  );

  // Filter levels based on search term
  const filteredLevelsByPhase = useMemo(() => {
    if (!searchTerm.trim()) return levelsByPhase;

    const term = searchTerm.toLowerCase();

    return Object.entries(levelsByPhase).reduce<PhaseMap>(
      (acc, [phase, data]) => {
        const filteredLevels = data.levels.filter(
          (level: ScopeAndSequenceLevel) => {
            // Check level number and description
            if (level.level.toString().includes(term)) return true;
            if (level.description?.toLowerCase().includes(term)) return true;

            // Check graphemes
            const graphemes = level.newGraphemes
              .map((id: GraphemeId) => getGraphemeById(id)?.spelling)
              .filter((g): g is string => Boolean(g));
            if (graphemes.some((g) => g.toLowerCase().includes(term)))
              return true;

            // Check words
            const words = [
              ...level.wordSet,
              ...level.wordChain,
              ...level.morphemeChain,
              ...level.trickyWords,
            ]
              .map((id: WordId) => getWordById(id)?.spelling)
              .filter((w): w is string => Boolean(w));
            if (words.some((w) => w.toLowerCase().includes(term))) return true;

            // Check sentences
            const sentences = level.sentences
              .map((id: SentenceId) => getSentenceById(id)?.spelling)
              .filter((s): s is string => Boolean(s));
            if (sentences.some((s) => s.toLowerCase().includes(term)))
              return true;

            return false;
          },
        );

        if (filteredLevels.length > 0) {
          acc[Number(phase)] = { ...data, levels: filteredLevels };
        }
        return acc;
      },
      {},
    );
  }, [levelsByPhase, searchTerm]);

  const handleAccordionChange = (newOpenPhases: string[]) => {
    // Track which phases were added or removed
    const added = newOpenPhases.filter((p) => !openPhases.includes(p));
    const removed = openPhases.filter((p) => !newOpenPhases.includes(p));

    added.forEach((phaseValue) => {
      const phaseNum = phaseValue.replace('phase-', '');
      const phaseData = levelsByPhase[Number(phaseNum)];
      if (phaseData) {
        trackMixpanelEvent('Scope and Sequence Phase Expanded', {
          phase: Number(phaseNum),
          levelRange: phaseData.levelRange,
        });
      }
    });

    removed.forEach((phaseValue) => {
      const phaseNum = phaseValue.replace('phase-', '');
      const phaseData = levelsByPhase[Number(phaseNum)];
      if (phaseData) {
        trackMixpanelEvent('Scope and Sequence Phase Collapsed', {
          phase: Number(phaseNum),
          levelRange: phaseData.levelRange,
        });
      }
    });

    setOpenPhases(newOpenPhases);
  };

  const handleGraphemeClick = async (grapheme: Grapheme, level: number) => {
    stopAudio();
    trackMixpanelEvent('Scope and Sequence Grapheme Audio Played', {
      grapheme: grapheme.spelling,
      pronunciation: grapheme.pronunciation,
      level,
    });
    await playPhonemeAudio(grapheme.pronunciation);
  };

  const handleWordClick = async (
    word: Word,
    level: number,
    wordType: string,
  ) => {
    stopAudio();
    trackMixpanelEvent('Scope and Sequence Word Audio Played', {
      word: word.spelling,
      wordType,
      level,
    });
    await soundOutScopeAndSequenceWord(word.spelling);
  };

  const handleTrickyWordClick = async (word: Word, level: number) => {
    stopAudio();
    trackMixpanelEvent('Scope and Sequence Word Audio Played', {
      word: word.spelling,
      wordType: 'trickyWords',
      level,
    });
    await playTrickyWordAudio(word.spelling.toLowerCase());
  };

  const handleSentenceClick = async (sentence: Sentence, level: number) => {
    stopAudio();
    const wordCount =
      sentence.words?.length || sentence.spelling.split(' ').length;
    trackMixpanelEvent('Scope and Sequence Sentence Audio Played', {
      sentence: sentence.spelling,
      wordCount,
      level,
    });
    await playSentenceAudio(sentence.spelling);
    await soundOutSentence(sentence.spelling, 0);
  };

  const handleGenerateLessonPlan = (level: number) => {
    const levelData = getLevelById(`level-${level}`);
    const phase = levelData ? getPhaseById(levelData.phase) : null;

    trackMixpanelEvent('Generate Lesson Plan from Scope and Sequence', {
      level,
      phase: phase?.phase,
    });

    if (standalone) {
      navigate(`/lessonplan?level=${level}`);
    } else {
      navigate(`/dashboard/lessonplans/new?level=${level}`);
    }
  };

  const handleGenerateCustomLevel = (level: number) => {
    if (!isCustomLevelsEnabled) {
      dispatch(setAttemptedProFeature('Custom Levels'));
      return;
    }

    const levelData = getLevelById(`level-${level}`);
    const phase = levelData ? getPhaseById(levelData.phase) : null;

    trackMixpanelEvent('Generate Custom Level from Scope and Sequence', {
      level,
      phase: phase?.phase,
    });

    navigate(`/dashboard/customlevels/new?level=${level}`);
  };

  const renderGraphemes = (graphemeIds: GraphemeId[], level: number) => {
    const graphemes = graphemeIds
      .map((graphemeId) => getGraphemeById(graphemeId))
      .filter((g) => !!g);
    return (
      <ItemsContainer>
        {graphemes.map((grapheme) => {
          const exampleWord = getWordById(grapheme.example);
          const item = (
            <Item
              key={grapheme.id}
              value={grapheme.spelling}
              type="grapheme"
              onClick={() => handleGraphemeClick(grapheme, level)}
            />
          );
          if (!exampleWord) return item;
          const word = exampleWord.spelling;
          const spelling = grapheme.spelling;
          const matchIndex = getGraphemeOffsetInWord(grapheme, exampleWord);
          const boldWord =
            matchIndex >= 0 ? (
              <>
                {word.slice(0, matchIndex)}
                <strong>
                  {word.slice(matchIndex, matchIndex + spelling.length)}
                </strong>
                {word.slice(matchIndex + spelling.length)}
              </>
            ) : (
              <strong>{word}</strong>
            );
          return (
            <Tooltip key={grapheme.id}>
              <TooltipTrigger asChild>{item}</TooltipTrigger>
              <TooltipContent className="text-sm">
                e.g. {boldWord}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </ItemsContainer>
    );
  };

  const renderWords = (
    wordIds: WordId[],
    level: number,
    wordType: string,
    isTricky?: boolean,
  ) => {
    const words = wordIds
      .map((wordId) => getWordById(wordId))
      .filter((w) => !!w);
    return (
      <ItemsContainer>
        {words.map((word) => {
          const morphemes = word.morphemes
            .map((id) => getMorphemeById(id))
            .filter((m) => !!m);
          const graphemes = getWordGraphemes(word);
          const imageSlug = word.example
            .toLowerCase()
            .replace(/[^a-z0-9 ]/g, '')
            .trim()
            .replace(/ +/g, '-');
          const imageUrl = `/game_assets/example_images/${imageSlug}.png`;
          return (
            <Tooltip key={word.id}>
              <TooltipTrigger asChild>
                <Item
                  value={word.spelling}
                  type={isTricky ? 'tricky' : 'word'}
                  onClick={() =>
                    isTricky
                      ? handleTrickyWordClick(word, level)
                      : handleWordClick(word, level, wordType)
                  }
                />
              </TooltipTrigger>
              <TooltipContent
                className="max-w-md w-80 p-0 overflow-hidden"
                side="bottom"
              >
                <div className="flex">
                  <img
                    src={imageUrl}
                    alt={word.spelling}
                    className="w-32 h-32 object-cover flex-shrink-0 bg-muted"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <div className="p-3 space-y-2 min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-base">{word.spelling}</p>
                      <button
                        className="p-1 rounded-md hover:bg-muted transition-colors cursor-pointer"
                        onPointerDown={(e) => {
                          e.preventDefault();
                          if (isTricky) {
                            handleTrickyWordClick(word, level);
                          } else {
                            handleWordClick(word, level, wordType);
                          }
                        }}
                      >
                        <SpeakerHighIcon className="size-4" />
                      </button>
                    </div>
                    {word.definition && (
                      <p className="text-sm">{word.definition}</p>
                    )}
                    {word.example && (
                      <p className="text-sm italic text-muted-foreground">
                        &ldquo;{word.example}&rdquo;
                      </p>
                    )}
                  </div>
                </div>
                <div className="px-3 pb-3 space-y-2">
                  {morphemes.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs text-muted-foreground">
                        Morphemes:
                      </span>
                      {morphemes.map((m, i) => (
                        <span
                          key={i}
                          className={`text-xs px-1.5 py-0.5 rounded ${morphemeTypeStyles[m.type]}`}
                        >
                          {m.spelling}
                        </span>
                      ))}
                    </div>
                  )}
                  {graphemes.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs text-muted-foreground">
                        Phonemes:
                      </span>
                      {graphemes.map((g, i) => (
                        <span key={i} className="text-sm font-mono">
                          {g.spelling}
                          <span className="text-muted-foreground">
                            {' '}
                            /{g.pronunciation}/
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </ItemsContainer>
    );
  };

  const renderChain = (wordIds: WordId[], level: number, wordType: string) => {
    const words = wordIds
      .map((wordId) => getWordById(wordId))
      .filter((w) => !!w);
    return (
      <ItemsContainer className="items-center">
        {words.map((word, index) => {
          const morphemes = word.morphemes
            .map((id) => getMorphemeById(id))
            .filter((m) => !!m);
          const graphemes = getWordGraphemes(word);
          const imageSlug = word.example
            .toLowerCase()
            .replace(/[^a-z0-9 ]/g, '')
            .trim()
            .replace(/ +/g, '-');
          const imageUrl = `/game_assets/example_images/${imageSlug}.png`;
          return (
            <span key={word.id} className="contents">
              {index > 0 && (
                <ArrowRightIcon className="size-3.5 text-muted-foreground flex-shrink-0" />
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Item
                    value={word.spelling}
                    type="chain"
                    onClick={() => handleWordClick(word, level, wordType)}
                  />
                </TooltipTrigger>
                <TooltipContent
                  className="max-w-md w-80 p-0 overflow-hidden"
                  side="bottom"
                >
                  <div className="flex">
                    <img
                      src={imageUrl}
                      alt={word.spelling}
                      className="w-32 h-32 object-cover flex-shrink-0 bg-muted"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="p-3 space-y-2 min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-base">
                          {word.spelling}
                        </p>
                        <button
                          className="p-1 rounded-md hover:bg-muted transition-colors cursor-pointer"
                          onPointerDown={(e) => {
                            e.preventDefault();
                            handleWordClick(word, level, wordType);
                          }}
                        >
                          <SpeakerHighIcon className="size-4" />
                        </button>
                      </div>
                      {word.definition && (
                        <p className="text-sm">{word.definition}</p>
                      )}
                      {word.example && (
                        <p className="text-sm italic text-muted-foreground">
                          &ldquo;{word.example}&rdquo;
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="px-3 pb-3 space-y-2">
                    {morphemes.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          Morphemes:
                        </span>
                        {morphemes.map((m, i) => (
                          <span
                            key={i}
                            className={`text-xs px-1.5 py-0.5 rounded ${morphemeTypeStyles[m.type]}`}
                          >
                            {m.spelling}
                          </span>
                        ))}
                      </div>
                    )}
                    {graphemes.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          Phonemes:
                        </span>
                        {graphemes.map((g, i) => (
                          <span key={i} className="text-sm font-mono">
                            {g.spelling}
                            <span className="text-muted-foreground">
                              {' '}
                              /{g.pronunciation}/
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </span>
          );
        })}
      </ItemsContainer>
    );
  };

  const renderSentences = (sentenceIds: SentenceId[], level: number) => {
    const sentences = sentenceIds
      .map((sentenceId) => getSentenceById(sentenceId))
      .filter((s) => !!s);
    return (
      <ItemsContainer>
        {sentences.map((sentence) => (
          <Item
            key={sentence.id}
            value={sentence.spelling}
            type="sentence"
            onClick={() => handleSentenceClick(sentence, level)}
          />
        ))}
      </ItemsContainer>
    );
  };

  const renderContent = () => (
    <Accordion
      type="multiple"
      value={openPhases}
      onValueChange={handleAccordionChange}
    >
      {Object.entries(filteredLevelsByPhase).map(
        ([phase, { levels, description }]) => (
          <AccordionItem
            key={phase}
            value={`phase-${phase}`}
            className="border-b-0"
          >
            <AccordionTrigger className="px-4 py-3 bg-muted/50 hover:bg-muted/70 rounded-lg mx-2 my-1">
              <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200" />
              <div className="flex flex-col text-left flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold">Phase {phase}</span>
                  <Badge variant="secondary" className="text-xs">
                    {levels.length} levels
                  </Badge>
                </div>
                <span className="text-sm text-muted-foreground">
                  {description}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="ml-6 border-l-2 border-muted pl-4 mr-4 mb-4">
                {levels.map((level: ScopeAndSequenceLevel, index: number) => (
                  <div key={level.level} className="py-3 group">
                    {index > 0 && <Separator className="mb-4 -mt-3" />}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                          Level {level.level}
                        </span>
                        {level.description && (
                          <span className="text-sm text-foreground">
                            {level.description}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 invisible group-hover:visible">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5 text-xs"
                          onClick={() => handleGenerateLessonPlan(level.level)}
                        >
                          <ChalkboardTeacherIcon className="size-4" />
                          <span>Lesson plan</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5 text-xs"
                          onClick={() => handleGenerateCustomLevel(level.level)}
                        >
                          <JoystickIcon className="size-4" />
                          <span>Custom level</span>
                          {!isCustomLevelsEnabled && <ProIcon />}
                        </Button>
                      </div>
                    </div>
                    <div className="grid gap-2 pl-1">
                      {level.games.length > 0 && (
                        <ContentRow>
                          <ContentLabel>Activities</ContentLabel>
                          <ItemsContainer>
                            {level.games.map((game) => {
                              const info = gameInfo[game];
                              if (!info) return null;
                              return (
                                <Tooltip key={game}>
                                  <TooltipTrigger asChild>
                                    <span
                                      className={`flex items-center gap-1.5 px-2 py-1 text-sm font-medium
                                        rounded-md border cursor-default transition-all
                                        hover:shadow-sm bg-slate-50 hover:bg-slate-100 border-slate-200`}
                                    >
                                      {info.name}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs p-3">
                                    <p className="font-semibold text-sm mb-1">
                                      {info.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {info.description}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })}
                          </ItemsContainer>
                        </ContentRow>
                      )}
                      {level.newGraphemes.length > 0 && (
                        <ContentRow>
                          <ContentLabel>New Graphemes</ContentLabel>
                          {renderGraphemes(level.newGraphemes, level.level)}
                        </ContentRow>
                      )}
                      {level.wordSet.length > 0 && (
                        <ContentRow>
                          <ContentLabel>Word Set</ContentLabel>
                          {renderWords(level.wordSet, level.level, 'wordSet')}
                        </ContentRow>
                      )}
                      {level.wordChain.length > 0 && (
                        <ContentRow>
                          <ContentLabel>Word Chain</ContentLabel>
                          {renderChain(
                            level.wordChain,
                            level.level,
                            'wordChain',
                          )}
                        </ContentRow>
                      )}
                      {level.morphemeChain.length > 0 && (
                        <ContentRow>
                          <ContentLabel>Morpheme Chain</ContentLabel>
                          {renderChain(
                            level.morphemeChain,
                            level.level,
                            'morphemeChain',
                          )}
                        </ContentRow>
                      )}
                      {level.sentences.length > 0 && (
                        <ContentRow>
                          <ContentLabel>Sentences</ContentLabel>
                          {renderSentences(level.sentences, level.level)}
                        </ContentRow>
                      )}
                      {level.trickyWords.length > 0 && (
                        <ContentRow>
                          <ContentLabel>Tricky Words</ContentLabel>
                          {renderWords(
                            level.trickyWords,
                            level.level,
                            'trickyWords',
                            true,
                          )}
                        </ContentRow>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ),
      )}
    </Accordion>
  );

  if (standalone) {
    return (
      <div className="min-h-screenflex flex-col">
        <MicroAppNav feedbackSource="scopeandsequence" />

        <div className="flex items-center justify-between sm:px-12 lg:px-14 mt-2">
          <span className="text-xl tracking-tight font-semibold">
            Scope & Sequence
          </span>
          <SearchInput
            placeholder="Search content..."
            value={searchTerm}
            onChange={setSearchTerm}
            alwaysOpen
          />
        </div>

        <div className="sm:px-6 lg:px-8">{renderContent()}</div>
      </div>
    );
  }

  return (
    <DashboardViewContainer>
      <DashboardViewHeader>
        <DashboardViewTitle>Scope and Sequence</DashboardViewTitle>
        <DashboardViewActions>
          <SearchInput
            placeholder="Search content..."
            value={searchTerm}
            onChange={setSearchTerm}
            alwaysOpen
          />
        </DashboardViewActions>
      </DashboardViewHeader>
      <DashboardViewContent>{renderContent()}</DashboardViewContent>
    </DashboardViewContainer>
  );
}

type ItemType =
  | 'grapheme'
  | 'morpheme'
  | 'word'
  | 'chain'
  | 'sentence'
  | 'tricky';

interface ItemProps {
  value: string;
  onClick: () => void;
  type: ItemType;
}

const itemTypeStyles: Record<ItemType, string> = {
  grapheme: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
  morpheme: 'bg-purple-50 hover:bg-purple-100 border-purple-200',
  word: 'bg-green-50 hover:bg-green-100 border-green-200',
  chain: 'bg-cyan-50 hover:bg-cyan-100 border-cyan-200',
  sentence: 'bg-amber-50 hover:bg-amber-100 border-amber-200',
  tricky: 'bg-red-50 hover:bg-red-100 border-red-200',
};

const morphemeTypeStyles: Record<MorphemeType, string> = {
  prefix: 'bg-orange-100 text-orange-800',
  base: 'bg-purple-100 text-purple-800',
  suffix: 'bg-teal-100 text-teal-800',
  connector: 'bg-slate-100 text-slate-800',
};

const Item = forwardRef<HTMLButtonElement, ItemProps>(
  ({ value, onClick, type, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={`flex items-center gap-1.5 px-2 py-1 text-sm font-medium
          rounded-md border cursor-pointer transition-all
          hover:shadow-sm active:scale-95 ${itemTypeStyles[type]}`}
        onClick={onClick}
        {...props}
      >
        {value}
      </button>
    );
  },
);

const ItemsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
  }
`;

const ContentLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: hsl(var(--muted-foreground));
  min-width: 120px;
  flex-shrink: 0;
`;
